import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ITariff} from '../../../models/ITariff';
import {
    fetchAddTariff,
    fetchDeleteTariff,
    fetchGetAllTariffs,
    fetchGetTariffById,
    fetchUpdateTariff,
} from './actions';

interface ITariffState {
    list: ITariff[];
    isLoading: boolean;
    current: ITariff | null;
}

const handlePending = (state: ITariffState) => {
    state.isLoading = true;
};

const handleRejected = (state: ITariffState) => {
    state.isLoading = false;
};

const initialState: ITariffState = {
    list: [],
    isLoading: false,
    current: null,
};

export const TariffSlice = createSlice({
    name: 'tariff_slice',
    initialState,
    reducers: {
        setTariffs: (state, action: PayloadAction<ITariff[]>) => {
            state.list = action.payload;
        },
        setCurrentTariff: (state, action: PayloadAction<ITariff | null>) => {
            state.current = action.payload;
        },
        addTariff: (state, action: PayloadAction<ITariff>) => {
            state.list = [...state.list, action.payload];
        },
        removeTariff: (state, action: PayloadAction<ITariff>) => {
            state.list = state.list.filter((t) => t.id !== action.payload.id);
        },
    },
    extraReducers: (builder) => {
        builder
            // ── Add ──
            .addCase(fetchAddTariff.fulfilled, (state, action: PayloadAction<ITariff>) => {
                state.list = [...state.list, action.payload];
                state.isLoading = false;
            })
            .addCase(fetchAddTariff.pending, handlePending)
            .addCase(fetchAddTariff.rejected, handleRejected)

            // ── Get all ──
            .addCase(fetchGetAllTariffs.fulfilled, (state, action: PayloadAction<ITariff[]>) => {
                state.list = action.payload.sort(
                    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
                );
                state.isLoading = false;
            })
            .addCase(fetchGetAllTariffs.pending, handlePending)
            .addCase(fetchGetAllTariffs.rejected, handleRejected)

            // ── Get by id ──
            .addCase(fetchGetTariffById.fulfilled, (state, action: PayloadAction<ITariff>) => {
                state.current = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchGetTariffById.pending, handlePending)
            .addCase(fetchGetTariffById.rejected, handleRejected)

            // ── Update ──
            .addCase(fetchUpdateTariff.fulfilled, (state, action: PayloadAction<ITariff>) => {
                state.list = state.list.map((t) =>
                    t.id === action.payload.id ? action.payload : t,
                );
                state.current = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchUpdateTariff.pending, handlePending)
            .addCase(fetchUpdateTariff.rejected, handleRejected)

            // ── Delete ──
            .addCase(fetchDeleteTariff.fulfilled, (state, action: PayloadAction<ITariff>) => {
                state.list = state.list.filter((t) => t.id !== action.payload.id);
                state.isLoading = false;
            })
            .addCase(fetchDeleteTariff.pending, handlePending)
            .addCase(fetchDeleteTariff.rejected, handleRejected);
    },
});

export const {
    setTariffs,
    setCurrentTariff,
    addTariff,
    removeTariff
} = TariffSlice.actions;

export default TariffSlice.reducer;